import * as Yup from 'yup';
import { AutocompleteChangeReason, Box, Switch } from '@mui/material';
import { ChangeEvent, SyntheticEvent } from 'react';
import { Classification, DocumentStatus } from '@synergy/models';
import { Field, FieldProps, Form, Formik, FormikProps } from 'formik';
import { FormattedMessage } from 'react-intl';
import { FormikPoolDatePicker, ScrollToFieldError } from '@synergy/components';
import { PaloDetailSlot, PaloField, PaloFormGrid, PaloSegmentedToggle } from '@synergy/components-palo';
import { PaloThemeProvider } from '@synergy/theme-palo';
import { connect } from 'react-redux';
import { fields, postProcessMarkdown } from '@synergy/utils';
import dayjs from 'dayjs';

import { AppState } from '../../../store/models/app-state';
import { ClassificationAutocomplete } from '../../../common/components/classification-autocomplete/classification-autocomplete';
import { Contact } from '../../../common/models/contact';
import {
    FormControlledStepComponent,
    FormControlledStepComponentProps,
} from '../../../common/components/abstract/form-controlled-step-component';
import { Inquiry } from '../models/inquiry';
import { PaloDirtyComparison } from '../../../common/components/palo/palo-dirty-context';
import { PaloFormikField } from '../../../common/components/palo/palo-formik-field';
import { PaloPositionsSlot } from '../../../common/components/palo/palo-positions-slot';
import { PaloRemarkPair } from '../../../common/components/palo/palo-remark-pair';
import { PaloTextControl } from '../../../common/components/palo/palo-text-control';
import {
    PartialContact,
    PoolContactAutocompleteList,
} from '../../../common/components/autocomplete-list/contact-autocomplete-list/pool-contact-autocomplete-list';
import { User } from '../../../common/models/user';
import { addWorkdays } from '../../../common/utils/add-workdays';
import { pickCommonPartial } from '../../../common/utils/pick-common-partial';
import { subtractWorkdays } from '../../../common/utils/subtract-workdays';

interface PaloInquiryDetailOverviewProps extends FormControlledStepComponentProps<Partial<Inquiry>> {
    contacts: Contact[];
    isNewInquiry: boolean;
    user: User;
}

interface InquiryDetailOverviewFormValues {
    title: string;
    responsibleContact?: Contact;
    internalNote: string;
    noteToSupplier: string;
    noteToCustomer: string;
    enableExpirationNotifications: boolean;
    endDate?: Date;
    dueDate?: Date;
    createdAt: Date;
    isAuction: boolean;
    classifications: Classification[];
}
const fieldNames = fields<InquiryDetailOverviewFormValues>();

/**
 * The inquiry's own data in two rows; the customers follow as cards under it,
 * so overview and positions are one page (#2252).
 */
const INQUIRY_HEAD_AREAS = [
    'title title responsibleContact endDate',
    // The reminder date sits under the end date, as wide as it; the switch
    // that unlocks it stands to its left (R-219).
    'classification classification reminder reminderDate',
];

/** The same head in three columns, for a narrow mask. */
const INQUIRY_HEAD_NARROW_AREAS = [
    'title title responsibleContact',
    'classification classification endDate',
    'reminder reminder reminderDate',
];

class PaloInquiryDetailOverviewBase extends FormControlledStepComponent<
    Partial<Inquiry>,
    InquiryDetailOverviewFormValues,
    PaloInquiryDetailOverviewProps
> {
    private defaultEndDate = addWorkdays(dayjs(), 2);

    protected initialValues: InquiryDetailOverviewFormValues = {
        title: '',
        responsibleContact: this.props.user.contact,
        internalNote: '',
        noteToSupplier: '',
        noteToCustomer: '',
        enableExpirationNotifications: false,
        endDate: undefined,
        dueDate: undefined,
        createdAt: new Date(),
        isAuction: false,
        classifications: [],
    };

    /**
     * The shape this step hands to the wizard. Also what the unsaved marker
     * compares in, on both sides — the normalising below would otherwise read as
     * a change on its own.
     */
    private toHandover = (values: InquiryDetailOverviewFormValues): Partial<Inquiry> => {
        const { dueDate: _dueDate, ...partialInquiryValues } = this.initialValues;
        const { dueDate, endDate, ...restValues } = values;
        const dayjsDueDate = dueDate ? dayjs(dueDate).hour(0).minute(0).second(0).millisecond(0).toDate() : undefined;

        return {
            ...pickCommonPartial(partialInquiryValues, restValues),
            endDate: endDate,
            dueDate: dayjsDueDate,
            noteToSupplier: postProcessMarkdown(values.noteToSupplier),
        };
    };

    protected onSubmit = (values: InquiryDetailOverviewFormValues) => {
        this.props.propagateInternalState(this.toHandover(values));
    };

    private toFormValues(state?: Partial<Inquiry>): InquiryDetailOverviewFormValues {
        // Allow all properties of the inquiry partial, unused ones are ignored
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const definedState: any = {};

        if (state) {
            Object.keys(state)
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .filter(key => !!(state as any)[key])
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .forEach(key => (definedState[key] = (state as any)[key]));

            if (state.dueDate) {
                definedState.dueDate = dayjs(state.dueDate);
            }
        }

        // Taken once per mask: worked out on every call, the form and the
        // state it is compared against held moments a few milliseconds apart,
        // and a new inquiry read as unsaved before anything was entered.
        if (this.props.isNewInquiry && !definedState.endDate) {
            definedState.endDate = this.defaultEndDate;
        }

        // Reduced to the fields of this step, the same way onSubmit hands them
        // back. Otherwise the document's extra fields would make every
        // comparison against it fail.
        return { ...this.initialValues, ...pickCommonPartial(this.initialValues, definedState) };
    }

    private updateInternalValuesByProps(props: PaloInquiryDetailOverviewProps) {
        if (!this.formikInstance) {
            return;
        }

        this.formikInstance.setValues(this.toFormValues(props.internalState));
    }

    public componentDidMount() {
        this.updateInternalValuesByProps(this.props);
    }

    public UNSAFE_componentWillReceiveProps(nextProps: PaloInquiryDetailOverviewProps) {
        if (this.props.internalState !== nextProps.internalState) {
            this.updateInternalValuesByProps(nextProps);
        }
    }

    private handleClassificationChange =
        (formikProps: FormikProps<InquiryDetailOverviewFormValues>) =>
        (_event: SyntheticEvent, values: Classification[], reason: AutocompleteChangeReason) => {
            if (reason === 'clear') {
                formikProps.setFieldValue(fieldNames.classifications, []);
            } else {
                formikProps.setFieldValue(fieldNames.classifications, values);
            }
        };

    private handleUrgencyChange =
        (formikProps: FormikProps<InquiryDetailOverviewFormValues>) => (_event: ChangeEvent, checked: boolean) => {
            if (checked) {
                formikProps.setFieldValue(
                    fieldNames.dueDate,
                    subtractWorkdays(dayjs(this.formikInstance?.values.endDate), 3)
                );
            } else {
                formikProps.setFieldValue(fieldNames.dueDate, null);
            }
            formikProps.setFieldValue(fieldNames.enableExpirationNotifications, checked);
        };

    public render() {
        const { contacts, unlocked } = this.props;

        const schema = Yup.object().shape({
            title: Yup.string().required('app.error.required'),
            endDate: Yup.date().required('app.error.required').typeError('app.error.required'),
            responsibleContact: Yup.mixed().required('app.error.required'),
        });

        const readonly =
            (!unlocked &&
                this.props.globalState &&
                this.props.globalState.status &&
                this.props.globalState.status !== DocumentStatus.Created) ||
            false;

        const readonlyContactAndDate =
            this.props.globalState?.status === DocumentStatus.Completed ||
            this.props.globalState?.status === DocumentStatus.CompletedWithTransition;

        return (
            <PaloThemeProvider>
                <Box>
                    <Formik
                        initialValues={this.initialValues}
                        onSubmit={this.onSubmit}
                        validationSchema={schema}
                        innerRef={ref => (this.formikInstance = ref)}
                        enableReinitialize={true}
                    >
                        {() => (
                            <Form>
                                <PaloDirtyComparison
                                    baseline={this.toHandover(this.toFormValues(this.props.documentState))}
                                    normalize={this.toHandover}
                                />
                                <ScrollToFieldError />

                                {!readonly && (
                                    <PaloDetailSlot name="kind">
                                        <Field name={fieldNames.isAuction}>
                                            {({ field, form }: FieldProps<boolean>) => (
                                                <PaloSegmentedToggle
                                                    value={field.value ?? false}
                                                    options={[
                                                        {
                                                            value: false,
                                                            label: (
                                                                <FormattedMessage id="app.inquiries.detail.isAuction.false" />
                                                            ),
                                                        },
                                                        {
                                                            value: true,
                                                            label: (
                                                                <FormattedMessage id="app.inquiries.detail.isAuction.true" />
                                                            ),
                                                        },
                                                    ]}
                                                    onChange={this.setFieldValue(form, field.name)}
                                                />
                                            )}
                                        </Field>
                                    </PaloDetailSlot>
                                )}
                                <PaloFormGrid
                                    columns={4}
                                    labels="side"
                                    areas={INQUIRY_HEAD_AREAS}
                                    narrowAreas={INQUIRY_HEAD_NARROW_AREAS}
                                >
                                    <PaloTextControl
                                        name={fieldNames.title}
                                        label={<FormattedMessage id="app.inquiries.overview.titleEmail" />}
                                        required
                                        area="title"
                                        autoFocus
                                        disabled={readonly}
                                    />
                                    <PaloFormikField<PartialContact>
                                        name={fieldNames.responsibleContact}
                                        label={<FormattedMessage id="app.common.responsibleContact" />}
                                        required
                                        area="responsibleContact"
                                    >
                                        {({ field, hasError }) => (
                                            <PoolContactAutocompleteList
                                                items={contacts}
                                                label={<></>}
                                                onSelected={value =>
                                                    this.formikInstance?.setFieldValue(field.name, value)
                                                }
                                                required={false}
                                                error={hasError}
                                                readOnly={readonlyContactAndDate}
                                                {...field}
                                            />
                                        )}
                                    </PaloFormikField>
                                    <PaloField
                                        label={
                                            <>
                                                <FormattedMessage id="app.label.endDate" /> *
                                            </>
                                        }
                                        name={fieldNames.endDate}
                                        area="endDate"
                                    >
                                        <FormikPoolDatePicker
                                            name={fieldNames.endDate}
                                            readOnly={readonlyContactAndDate}
                                            disablePast={true}
                                        />
                                    </PaloField>
                                    <Field name={fieldNames.classifications}>
                                        {({ field, form }: FieldProps<Classification[]>) => (
                                            <PaloField
                                                label={<FormattedMessage id="app.header.classification" />}
                                                name={field.name}
                                                area="classification"
                                            >
                                                <ClassificationAutocomplete
                                                    value={field.value}
                                                    onChange={this.handleClassificationChange(form)}
                                                />
                                            </PaloField>
                                        )}
                                    </Field>
                                    {/* Within sight of the end date it is derived from —
                                        down between the two remarks it was set and then
                                        forgotten (#2185). */}
                                    <PaloField
                                        label={
                                            <>
                                                {/* The section title it had, now that it has no section. */}
                                                <FormattedMessage id="app.header.urgency" />
                                                {this.formikInstance?.values.enableExpirationNotifications ? ' *' : ''}
                                            </>
                                        }
                                        name={fieldNames.enableExpirationNotifications}
                                        area="reminder"
                                    >
                                        <Field name={fieldNames.enableExpirationNotifications}>
                                            {({ field, form }: FieldProps<boolean>) => (
                                                <Switch
                                                    name={field.name}
                                                    onChange={this.handleUrgencyChange(form)}
                                                    onBlur={field.onBlur}
                                                    checked={field.value}
                                                    color="primary"
                                                    size="small"
                                                    disabled={readonly}
                                                    inputProps={{ 'aria-label': 'Lieferant erinnern' }}
                                                />
                                            )}
                                        </Field>
                                    </PaloField>
                                    <PaloField label="" name={fieldNames.dueDate} area="reminderDate">
                                        <FormikPoolDatePicker
                                            name={fieldNames.dueDate}
                                            readOnly={readonly}
                                            disablePast={true}
                                            disabled={!this.formikInstance?.values.enableExpirationNotifications}
                                        />
                                    </PaloField>
                                </PaloFormGrid>
                                {/* The customers with their positions, from the next
                                    step of the same tab (#2252). */}
                                <PaloPositionsSlot />
                                <PaloRemarkPair
                                    external={{
                                        name: fieldNames.noteToSupplier,
                                        label: <FormattedMessage id="app.header.noteToSupplier" />,
                                    }}
                                    internalName={fieldNames.internalNote}
                                    readonly={readonly}
                                />
                            </Form>
                        )}
                    </Formik>
                </Box>
            </PaloThemeProvider>
        );
    }
}

const mapStateToProps = (state: AppState) => ({
    contacts: state.contacts.sortedByName,
    isNewInquiry: !state.inquiries.requesting && state.inquiries.entity === undefined,
    user: state.user,
});

export const PaloInquiryDetailOverview = connect(mapStateToProps)(PaloInquiryDetailOverviewBase);
